// assets/quiz-engine.js

// Endurance-ish sports that earn electrolytes regardless of session count (spec §3.1)
const ENDURANCE_SPORTS = new Set(['endurance', 'team', 'mixed']);

// Every user should leave with a substantial stack, not a thin 1-2 product
// recommendation. If the goal's base template yields fewer than this many
// categories, selectTemplate backfills from BACKFILL_BY_GOAL below.
// Wellbeing is lower (3) so user-chosen priorities drive the stack rather
// than generic backfill — otherwise every wellbeing persona converges on
// the same 4-item pack regardless of what they asked for.
const MIN_CORE_ITEMS = 4;
const MIN_CORE_ITEMS_BY_GOAL = {
  muscle: 4,
  fat_loss: 4,
  performance: 4,
  wellbeing: 3,
};

// Categories appended to a short template, in preference order, to bring
// the core stack up to MIN_CORE_ITEMS. These are the same picks that would
// otherwise appear in the "also consider" sidebar — promoting them to core
// lifts the recommendation from thin to complete.
const BACKFILL_BY_GOAL = {
  muscle:      ['omega', 'multivitamin', 'sleep'],
  fat_loss:    ['sleep', 'omega'],
  performance: ['omega', 'multivitamin', 'sleep', 'joint'],
  wellbeing:   ['omega', 'sleep', 'joint'],
};

// Goals where a pre-workout is a standard part of the core stack. Muscle-
// building and performance-training users benefit from pre-workout fuel on
// session days. Fat-loss users get a fat_burner as their stim — doubling up
// on pre-workout adds stimulant load and clutters the stack, so pre-workout
// stays in the fat-loss "Also consider" sidebar instead. Wellbeing users
// see pre-workout via the "energy" priority.
const PREWORKOUT_CORE_GOALS = new Set(['muscle', 'performance']);

// Wellbeing-branch adjunct cap (spec §3.1)
const WELLBEING_ADJUNCT_CAP = 2;

// Upgrade-pick budget band threshold (spec §3.2)
// All budgets above lt_30 qualify for the upgrade-pick (preserves protein
// slot from suppression when user already takes protein). Only £60+/week
// actually resolves at premium tier — at £30-60 the slot is kept but
// resolved at the user's normal tier so XL (£34.99) shows, not Efectiv (£59.99).
const UPGRADE_QUALIFYING_BUDGETS = new Set(['30_60', '60_100', '100_plus', 'best']);
// Only £100+/week triggers the premium Efectiv Whey upgrade.
// Mid-range (£60-100) and 'best' keep the default protein pick (XL Xtra Whey
// via trusted tier) — our highest-rated whey at 4.8 / 1,753 reviews.
// Users can still manually switch tier from the always-visible SWITCH TIER row.
const PREMIUM_UPGRADE_BUDGETS = new Set(['100_plus']);

export function selectTemplate(answers, products) {
  const template = products.templates[answers.goal];
  if (!template) throw new Error(`Unknown goal: ${answers.goal}`);

  const expanded = template.flatMap(category => {
    if (category === '__sport_driven__') {
      const sport = answers.sport;
      const chosen = products.sport_rules[sport];
      if (!chosen) throw new Error(`Unknown sport: ${sport}`);
      return [chosen];
    }
    if (category === '__priority_driven__') {
      const priorities = answers.priorities || [];
      const categories = [...new Set(priorities
        .map(p => products.priority_to_category[p])
        .filter(Boolean))];
      // Spec §3.1 — cap wellbeing adjuncts at 2
      return categories.slice(0, WELLBEING_ADJUNCT_CAP);
    }
    // Carb powder: only for endurance sport in performance branch
    if (category === '__endurance_carb__') {
      const isEndurance = answers.sport === 'endurance';
      return isEndurance ? ['carb_powder'] : [];
    }
    return [category];
  });

  // Dedup across the whole template. Priority-driven wellbeing branches can
  // map a user priority (e.g. 'energy' → multivitamin) onto a category that
  // already sits in the base template, producing a duplicate row in the stack.
  // First-occurrence wins so ordering is preserved.
  const dedup = [];
  const dedupSeen = new Set();
  for (const c of expanded) {
    if (!dedupSeen.has(c)) { dedup.push(c); dedupSeen.add(c); }
  }

  // Spec §3.1 — performance electrolytes: only if 5+ sessions OR endurance-ish sport
  let filtered = dedup;
  if (answers.goal === 'performance') {
    const highVolume = answers.sessions_per_week === '5_plus';
    const endurance = ENDURANCE_SPORTS.has(answers.sport);
    if (!highVolume && !endurance) {
      filtered = filtered.filter(c => c !== 'electrolytes');
    }
  }

  // Spec §3.2 — upgrade-pick: if user already has protein AND budget > £30/week,
  // swap the whey slot for a premium whey SKU marker regardless of goal-tier.
  const hasProtein = Array.isArray(answers.current_stack) && answers.current_stack.includes('protein');
  if (hasProtein && UPGRADE_QUALIFYING_BUDGETS.has(answers.budget)) {
    filtered = filtered.map(c => (c === 'protein' ? '__upgrade_protein__' : c));
  }

  // Mass gainer: muscle goal + user finds it hard to gain weight.
  // Mass gainer IS protein-plus-carbs — replace the protein/upgrade-protein
  // slot rather than adding a second protein powder alongside whey.
  if (answers.goal === 'muscle' && answers.gaining_difficulty === 'yes') {
    const hasProteinSlot = filtered.some(c => c === 'protein' || c === '__upgrade_protein__');
    if (hasProteinSlot) {
      filtered = filtered.map(c =>
        (c === 'protein' || c === '__upgrade_protein__') ? 'mass_gainer' : c
      );
    } else if (!filtered.includes('mass_gainer')) {
      filtered = [...filtered, 'mass_gainer'];
    }
  }

  // Pre-workout: standard in training-focused stacks. Any user who picked
  // muscle / fat loss / performance is training with intent and benefits
  // from pre-workout fuel on session days. Added here (not via backfill)
  // so it survives to core even when the base template already fills
  // MIN_CORE_ITEMS. The UI lets users untick it if they don't want it.
  if (PREWORKOUT_CORE_GOALS.has(answers.goal) && !filtered.includes('preworkout')) {
    filtered.push('preworkout');
  }

  // Ensure at least minCore categories in the stack. Short templates
  // (e.g. muscle = protein+creatine, or wellbeing with few priorities) get
  // topped up from goal-appropriate backfill so every user leaves with a
  // substantial recommendation. Wellbeing uses a lower minimum so
  // user-chosen priorities aren't diluted by generic filler.
  const minCore = MIN_CORE_ITEMS_BY_GOAL[answers.goal] ?? MIN_CORE_ITEMS;
  const seen = new Set(filtered.map(c => c === '__upgrade_protein__' ? 'protein' : c));
  const backfill = BACKFILL_BY_GOAL[answers.goal] || [];
  for (const cat of backfill) {
    if (filtered.length >= minCore) break;
    if (!seen.has(cat)) {
      filtered.push(cat);
      seen.add(cat);
    }
  }

  return filtered;
}

export function filterOptionsForBranch(options, branchId) {
  if (!Array.isArray(options)) return [];
  return options.filter(opt => {
    // No `branches` key = available in every branch
    if (!Array.isArray(opt.branches)) return true;
    return opt.branches.includes(branchId);
  });
}

const TIER_CASCADE = ['budget', 'trusted', 'premium'];

// Diet restrictions filter candidates by per-product diet_flags (populated by
// scripts/sync-diet-flags.mjs from Shopify tags/collections). When a product
// has no flag entry we exclude it for strict diets (conservative — better to
// drop the category than serve a non-compliant product). Gluten-free is
// permissive because most supplements in our catalogue are naturally GF.
function filterByDiet(candidates, diet, dietFlags) {
  if (!candidates || !candidates.length) return [];
  if (!diet || !diet.length || diet.includes('none')) return candidates;

  const strict = diet.some(d => d === 'vegan' || d === 'vegetarian' || d === 'dairy_free');
  const onlyGluten = !strict && diet.includes('gluten_free');

  return candidates.filter(h => {
    const f = dietFlags?.[h];
    if (!f) return onlyGluten; // missing flags → include only for permissive diets
    if (diet.includes('vegan') && !f.vegan) return false;
    if (diet.includes('vegetarian') && !f.vegetarian) return false;
    if (diet.includes('dairy_free') && !f.dairy_free) return false;
    if (diet.includes('gluten_free') && !f.gluten_free) return false;
    return true;
  });
}

// Resolve the preference sub-map for a category (clear whey / female multivit).
// Diet restrictions are applied via filterByDiet rather than sub-map selection.
// When the user has a strict diet we skip preference sub-maps and go straight
// to `default` — the preference would be a subset that likely excludes all
// compatible products.
// Returns the tier ('budget'/'trusted'/'premium') where `handle` is listed
// in the category's effective sub-map (respecting diet / clear-whey /
// female-multivitamin rules). Used by buildStack so the SWITCH TIER UI
// reflects the actual tier of the resolved product rather than the tier
// the user's budget answer asked for (which matters when cascade lifts
// a handle up from trusted/premium because the budget pick was OOS).
export function homeTierOf(handle, category, diet, products, answers) {
  const sub = pickCategorySubMap(category, diet, products, answers);
  if (!sub) return null;
  const { tierMap } = sub;
  for (const tier of TIER_CASCADE) {
    if ((tierMap[tier] || []).includes(handle)) return tier;
  }
  return null;
}

function pickCategorySubMap(category, diet, products, answers) {
  const catMap = products.categories[category];
  if (!catMap) return null;

  const hasStrictDiet = diet && diet.some(d =>
    d === 'vegan' || d === 'vegetarian' || d === 'dairy_free'
  );

  let effectiveDietKey = 'default';
  if (!hasStrictDiet) {
    if (answers?.protein_type === 'clear' && category === 'protein' && catMap.clear) {
      effectiveDietKey = 'clear';
    } else if (answers?.gender === 'female' && category === 'multivitamin' && catMap.female) {
      effectiveDietKey = 'female';
    }
  }

  const tierMap = catMap[effectiveDietKey];
  if (!tierMap) return null;
  return { tierMap, effectiveDietKey };
}

export function resolveCandidates(category, startTier, diet, products, answers) {
  const sub = pickCategorySubMap(category, diet, products, answers);
  if (!sub) return [];
  const { tierMap } = sub;
  const dietFlags = products.diet_flags;

  const startIdx = TIER_CASCADE.indexOf(startTier);
  for (let i = startIdx; i < TIER_CASCADE.length; i++) {
    const filtered = filterByDiet(tierMap[TIER_CASCADE[i]], diet, dietFlags);
    if (filtered.length) return filtered;
  }
  for (let i = startIdx - 1; i >= 0; i--) {
    const filtered = filterByDiet(tierMap[TIER_CASCADE[i]], diet, dietFlags);
    if (filtered.length) return filtered;
  }
  return [];
}

// Returns the FULL ordered list of candidates for a category across all tiers,
// most-preferred first. Used by buildStack so that if every handle in the start
// tier is OOS we can still surface a product from a higher/lower tier instead
// of dropping the category from the user's stack.
// Order: startTier, higher tiers (up), then lower tiers (down).
export function cascadedCandidates(category, startTier, diet, products, answers) {
  const sub = pickCategorySubMap(category, diet, products, answers);
  if (!sub) return [];
  const { tierMap } = sub;
  const dietFlags = products.diet_flags;
  const startIdx = TIER_CASCADE.indexOf(startTier);
  const order = [];
  if (startIdx >= 0) order.push(startIdx);
  for (let i = startIdx + 1; i < TIER_CASCADE.length; i++) order.push(i);
  for (let i = startIdx - 1; i >= 0; i--) order.push(i);

  const seen = new Set();
  const out = [];
  for (const idx of order) {
    const tier = TIER_CASCADE[idx];
    const filtered = filterByDiet(tierMap[tier], diet, dietFlags);
    for (const h of filtered) {
      if (!seen.has(h)) { seen.add(h); out.push(h); }
    }
  }
  return out;
}

export function tierForBudget(budget) {
  const map = {
    lt_30: 'budget',
    '30_60': 'budget',
    '60_100': 'trusted',
    '100_plus': 'trusted',
    best: 'trusted',
  };
  if (!map[budget]) throw new Error(`Unknown budget: ${budget}`);
  return map[budget];
}

// Map current_stack answer values to engine categories
const CURRENT_TO_CATEGORY = {
  protein: 'protein',
  creatine: 'creatine',
  multivit: 'multivitamin',
  omega: 'omega',
  // preworkout not in any default template, no suppression
};

const MIN_STACK_SIZE = 2;

export function suppressDuplicates(template, currentStack) {
  // No suppression — users who already take a product still need to buy it.
  // The current_stack data feeds the upgrade-pick (protein tier swap) and
  // analytics, but never removes categories from the recommended stack.
  return template;
}

export function recommendedQuantity(category, answers) {
  if (category !== 'protein') return 1;
  const bw = answers.bodyweight;
  const sessions = answers.sessions_per_week;
  // Heavy users (95+kg) need 2 tubs regardless of session count — bodyweight
  // drives protein requirement as much as training volume. A 100kg man
  // training 3x/week still needs ~2g/kg protein → 2 tubs over a month.
  if (bw === '95_plus') return 2;
  // Mid-weight users (80-95kg) only need 2 tubs when training volume is high.
  if (bw === '80_95' && sessions === '5_plus') return 2;
  return 1;
}

export const BUDGET_CAPS = {
  lt_30: 3000,
  '30_60': 6000,
  '60_100': 10000,
  '100_plus': 15000,
  best: Infinity,
};

// Categories that are never dropped, by goal
const MANDATORY = {
  muscle: (cat, opts) => cat === 'protein' || (cat === 'creatine' && opts.tenureMature),
  fat_loss: (cat) => cat === 'protein',
  performance: (cat) => cat === 'protein',
  wellbeing: () => false,
};

// Drop priority (first to drop)
const DROP_ORDER = ['multivitamin', 'omega', 'fibre', 'joint', 'sleep', 'electrolytes', 'beta_alanine', 'creatine', 'protein'];
const dropIdx = (c) => {
  const i = DROP_ORDER.indexOf(c);
  return i === -1 ? Infinity : i;
};

export function applyBudgetCap(items, budget, goal, opts = {}) {
  const capBase = BUDGET_CAPS[budget];
  if (capBase === Infinity) return { items, dropped: [], overBudget: false };
  const cap = Math.round(capBase * 1.1);
  const total = items.reduce((s, i) => s + (i.price * i.quantity), 0);
  // Warn-only: never drop items from the stack. Budget guides tier
  // selection (tierForBudget) — the cap just flags when the total
  // exceeds the user's stated budget so the UI can show a warning.
  return { items: [...items], dropped: [], overBudget: total > cap };
}

export async function buildStack(answers, products, productResolver) {
  const template = selectTemplate(answers, products);
  const suppressed = suppressDuplicates(template, answers.current_stack || []);
  const tier = tierForBudget(answers.budget);
  const diet = answers.diet || ['none'];

  // Cache to avoid re-resolving the same handle twice.
  // Wrap in Promise.resolve so synchronous test stubs also work.
  const resolveCache = new Map();
  const resolve = (h) => {
    if (!resolveCache.has(h)) {
      resolveCache.set(h, Promise.resolve().then(() => productResolver(h)));
    }
    return resolveCache.get(h);
  };

  // Build task list per category, then run in parallel.
  // Use cascadedCandidates (not resolveCandidates) so that if every handle in
  // the preferred tier is OOS we still try higher/lower tiers before dropping
  // the category. Preferred tier's handles come first so normal selection is
  // unchanged when stock is healthy.
  // Categories where we always default to the budget tier regardless of the
  // user's budget answer (value-first). Rationale: for these categories our
  // own-brand budget products are typically the right pick for most users
  // (high margin for us, low cost for them, no meaningful efficacy difference
  // vs. premium at the dose most people take). Users can still manually
  // switch via the always-visible SWITCH TIER row.
  const valueFirstSet = new Set(products.value_first_categories || []);

  const tasks = suppressed.map((rawCategory) => {
    const isUpgrade = rawCategory === '__upgrade_protein__';
    let category = isUpgrade ? 'protein' : rawCategory;
    let effectiveTier;
    if (isUpgrade) {
      effectiveTier = PREMIUM_UPGRADE_BUDGETS.has(answers.budget) ? 'premium' : tier;
    } else if (valueFirstSet.has(category)) {
      effectiveTier = 'budget';
    } else {
      effectiveTier = tier;
    }
    let candidates = cascadedCandidates(category, effectiveTier, diet, products, answers);

    // Diet-aware fallback: mass_gainer is replaced into the template when
    // the user reports difficulty gaining weight, but we don't stock a
    // vegan / dairy-free mass gainer. Rather than dropping the protein
    // slot entirely for those users, fall back to regular protein so the
    // stack still has its primary anchor product.
    if (!candidates.length && category === 'mass_gainer') {
      category = 'protein';
      candidates = cascadedCandidates('protein', effectiveTier, diet, products, answers);
    }

    return { rawCategory, category, isUpgrade, effectiveTier, candidates };
  });

  // Kick off all first-candidate resolutions in parallel
  const firstResolves = tasks.map(t =>
    t.candidates.length ? resolve(t.candidates[0]).catch(() => null) : Promise.resolve(null)
  );
  const firstResults = await Promise.all(firstResolves);

  const items = [];
  const exclusions = [];

  for (let i = 0; i < tasks.length; i++) {
    const t = tasks[i];
    if (!t.candidates.length) {
      console.warn(`[quiz-engine] ${t.category}: NO candidates — excluding`);
      exclusions.push({ category: t.category, reason: 'no_candidates_matching_diet' });
      continue;
    }
    let product = firstResults[i];
    if (!product || !product.available) {
      // Try remaining candidates in parallel, take first available
      const rest = t.candidates.slice(1);
      if (rest.length) {
        const restResults = await Promise.all(
          rest.map(h => resolve(h).catch(() => null))
        );
        product = restResults.find(p => p && p.available) || null;
      }
      if (!product || !product.available) {
        console.warn(`[quiz-engine] ${t.category}: all candidates unavailable — excluding`);
        exclusions.push({ category: t.category, reason: 'out_of_stock' });
        continue;
      }
    }
    const review = (products.reviews || {})[product.handle] || {};
    const usps = (products.usps || {})[product.handle] || [];
    // Use the actual tier the product lives in, not the requested tier.
    // Prevents the SWITCH TIER chip from showing "Budget" highlighted on
    // a product that was only reached by cascading up from an OOS budget
    // pick (e.g. XL Creatine OOS → lands on Efectiv, which is trusted).
    const resolvedTier = homeTierOf(product.handle, t.category, diet, products, answers)
      || t.effectiveTier;
    items.push({
      category: t.category, handle: product.handle, price: product.price,
      compareAt: product.compareAtPrice,
      title: product.title,
      image: product.featuredImage,
      quantity: recommendedQuantity(t.category, answers),
      product, tier: resolvedTier, upgradePick: t.isUpgrade,
      rating: review.rating || 0, reviewCount: review.count || 0,
      usps,
    });
  }

  const opts = { tenureMature: ['6m_24m', '2y_plus'].includes(answers.tenure) };
  const capped = applyBudgetCap(items, answers.budget, answers.goal, opts);
  const individualPicks = getIndividualPicks(suppressed, answers);

  return {
    items: capped.items,
    dropped: capped.dropped,
    overBudget: capped.overBudget,
    exclusions,
    goal: answers.goal,
    tier,
    individualPicks,
  };
}

// Map current_stack values to quiz categories for cross-sell
const CROSS_SELL_MAP = {
  preworkout: 'preworkout',
};

// Default cross-sell suggestions per goal — shown as "Also consider"
// picks when they aren't already in the user's core stack.
const DEFAULT_CROSS_SELL_BY_GOAL = {
  muscle:      ['omega', 'multivitamin', 'sleep'],
  // Pre-workout moved here from fat-loss core (see PREWORKOUT_CORE_GOALS).
  // Three picks so the sidebar has the same density as other goals.
  fat_loss:    ['preworkout', 'sleep', 'omega'],
  performance: ['omega', 'multivitamin', 'sleep', 'joint'],
  wellbeing:   [],
};

// Male-specific cross-sells — prepended to defaults for relevant goals so
// testosterone support surfaces for men chasing muscle/performance gains.
// Female users never see test_booster; it's ZMA/T-support territory.
const MALE_CROSS_SELL_BY_GOAL = {
  muscle:      ['test_booster'],
  performance: ['test_booster'],
};

const MAX_INDIVIDUAL_PICKS = 3;

// Signature: getIndividualPicks(template, answersOrCurrentStack)
// - answers object: preferred; enables goal-driven defaults
// - array: legacy current_stack; falls back to CROSS_SELL_MAP behaviour
export function getIndividualPicks(template, answersOrCurrentStack) {
  const templateSet = new Set(template);
  // Normalize upgrade-marker to its underlying category
  const tSet = new Set([...templateSet].map(c => c === '__upgrade_protein__' ? 'protein' : c));

  // Legacy array form (kept for tests)
  if (Array.isArray(answersOrCurrentStack)) {
    const currentStack = answersOrCurrentStack;
    if (!currentStack || currentStack.includes('none')) return [];
    return currentStack
      .map(v => CROSS_SELL_MAP[v])
      .filter(cat => cat && !tSet.has(cat))
      .slice(0, MAX_INDIVIDUAL_PICKS);
  }

  const answers = answersOrCurrentStack || {};
  const goal = answers.goal;
  const currentStack = answers.current_stack || [];
  const picks = [];

  // 1) Male-specific cross-sells first (test_booster for muscle/performance)
  if (answers.gender === 'male') {
    const maleCats = MALE_CROSS_SELL_BY_GOAL[goal] || [];
    for (const cat of maleCats) {
      if (!tSet.has(cat) && !picks.includes(cat)) picks.push(cat);
    }
  }

  // 2) Goal-based defaults (excluding anything already in the stack)
  const defaults = DEFAULT_CROSS_SELL_BY_GOAL[goal] || [];
  for (const cat of defaults) {
    if (!tSet.has(cat) && !picks.includes(cat)) picks.push(cat);
  }

  // 3) Anything the user said they take that isn't in the stack
  if (!currentStack.includes('none')) {
    for (const v of currentStack) {
      const cat = CROSS_SELL_MAP[v];
      if (cat && !tSet.has(cat) && !picks.includes(cat)) picks.push(cat);
    }
  }

  return picks.slice(0, MAX_INDIVIDUAL_PICKS);
}

if (typeof window !== 'undefined') {
  window.buildStack = buildStack;
  window.BUDGET_CAPS = BUDGET_CAPS;
  window.resolveCandidates = resolveCandidates;
  window.cascadedCandidates = cascadedCandidates;
  window.applyBudgetCap = applyBudgetCap;
  window.filterOptionsForBranch = filterOptionsForBranch;
  window.getIndividualPicks = getIndividualPicks;
}
